(function () {
  'use strict';

  var TEMPLATE_SELECTOR = '[data-video-template]';
  var LOADING_CLASS = 'is-loading';
  var ID_ATTR = 'data-video-id';

  var hlsLibraryPromise = null;

  function supportsNativeHls(video) {
    return !!video.canPlayType('application/vnd.apple.mpegurl');
  }

  // Loaded once per page, and only for browsers that need it.
  function loadHlsLibrary(src) {
    if (hlsLibraryPromise) return hlsLibraryPromise;

    hlsLibraryPromise = new Promise(function (resolve) {
      if (window.Hls) {
        resolve(window.Hls);
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.onload = function () { resolve(window.Hls || null); };
      script.onerror = function () { resolve(null); };
      document.head.appendChild(script);
    });

    return hlsLibraryPromise;
  }

  if (customElements.get('video-background')) return;

  customElements.define('video-background', class extends HTMLElement {
    connectedCallback() {
      this.videoId = this.getAttribute(ID_ATTR);
      this.videoTemplate = this.querySelector(TEMPLATE_SELECTOR);
      this.video = null;
      this.hls = null;
      this.powerSaverVideoPlay = this.powerSaverVideoPlay.bind(this);

      if (!this.videoId) return;

      // Hold the clip back until the rest of the page is done, so a multi-
      // megabyte hero video doesn't compete with the critical page assets.
      if (document.readyState === 'complete') {
        this.renderVideo();
      } else {
        window.addEventListener('load', function () { this.renderVideo(); }.bind(this), { once: true });
      }
    }

    powerSaverVideoPlay() {
      if (this.video) this.video.play();
    }

    renderVideo() {
      this.videoTemplateObserver = new IntersectionObserver(function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          this.mountVideo();
        }.bind(this));
      }.bind(this), { root: null, rootMargin: '300px', threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });

      this.videoTemplateObserver.observe(this);
    }

    mountVideo() {
      if (!this.videoTemplate) return;

      var holder = document.createElement('div');
      holder.innerHTML = this.videoTemplate.innerHTML;

      var video = holder.querySelector('video');
      var hlsSrc = video ? video.getAttribute('data-hls') : null;
      var mp4Src = video ? video.getAttribute('data-mp4') : null;
      var libSrc = video ? video.getAttribute('data-hls-lib') : null;

      // Chrome and Firefox would pick the progressive MP4 and pull the whole
      // file down. Strip the sources before the element enters the document so
      // autoplay has nothing to start on, then hand the stream to hls.js.
      var useHlsJs = !!(video && hlsSrc && libSrc && !supportsNativeHls(video));

      if (useHlsJs) {
        Array.prototype.forEach.call(video.querySelectorAll('source'), function (source) {
          source.remove();
        });
      }

      this.replaceChildren.apply(this, Array.prototype.slice.call(holder.childNodes));
      this.classList.remove(LOADING_CLASS);
      this.video = this.querySelector('video');

      if (!this.video) return;

      this.observeVideoPlayToggle();

      if (useHlsJs) {
        this.attachHls(hlsSrc, libSrc, mp4Src);
      } else {
        this.playVideo();
      }
    }

    attachHls(hlsSrc, libSrc, mp4Src) {
      loadHlsLibrary(libSrc).then(function (Hls) {
        if (!this.video) return;

        if (!Hls || !Hls.isSupported()) {
          this.playMp4(mp4Src);
          return;
        }

        // capLevelToPlayerSize keeps a 1080p master out of a 375px-wide phone
        // hero; the short buffer stops it pre-loading the entire loop.
        var hls = new Hls({ capLevelToPlayerSize: true, maxBufferLength: 10 });
        this.hls = hls;

        hls.on(Hls.Events.MANIFEST_PARSED, function () { this.playVideo(); }.bind(this));

        hls.on(Hls.Events.ERROR, function (event, data) {
          if (!data || !data.fatal) return;
          hls.destroy();
          this.hls = null;
          this.playMp4(mp4Src);
        }.bind(this));

        hls.loadSource(hlsSrc);
        hls.attachMedia(this.video);
      }.bind(this));
    }

    playMp4(mp4Src) {
      if (!this.video || !mp4Src) return;
      this.video.src = mp4Src;
      this.playVideo();
    }

    playVideo() {
      if (!this.video || typeof this.video.play !== 'function') return;

      var started = this.video.play();
      if (!started || typeof started.catch !== 'function') return;

      started.catch(function () {
        // Low power mode blocks autoplay until the visitor interacts.
        document.addEventListener('click', this.powerSaverVideoPlay, { passive: true, once: true });
        document.addEventListener('touchstart', this.powerSaverVideoPlay, { passive: true, once: true });
      }.bind(this));
    }

    observeVideoPlayToggle() {
      if (!this.video) return;

      this.videoPlayObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!this.video) return;

          if (entry.isIntersecting) {
            this.playVideo();
          } else if (typeof this.video.pause === 'function') {
            this.video.pause();
          }
        }.bind(this));
      }.bind(this), { rootMargin: '0px', threshold: [0, 1] });

      this.videoPlayObserver.observe(this.video);
    }

    disconnectedCallback() {
      if (this.videoTemplateObserver) this.videoTemplateObserver.disconnect();
      if (this.videoPlayObserver) this.videoPlayObserver.disconnect();

      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }

      if (this.videoId) {
        document.removeEventListener('click', this.powerSaverVideoPlay);
        document.removeEventListener('touchstart', this.powerSaverVideoPlay);
      }
    }
  });
})();
